import React, { useEffect, useState } from 'react';
import { momentService } from '../../../services/momentService';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';
import { Alert } from '../../ui/Alert';
import { AttachmentGallery } from '../moments/AttachmentGallery';
import { OriginBadge } from '../moments/OriginBadge';
import { MomentModals } from '../moments/MomentModals';
import { useMomentEditor } from '../moments/useMomentEditor';
import { LegacyMemories } from './LegacyMemories';
import { ConnectionInsights } from './ConnectionInsights';
import { formatMomentDate, formatMomentTime } from '../../../utils/momentDates';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const ConnectionMoments = ({ lead, user }) => {
  const contactId = lead.contact_id;
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(!!contactId);
  const [loadError, setLoadError] = useState('');
  const editor = useMomentEditor({ user, setMoments });
  const { success, warning, setFormState, setViewing } = editor;

  useEffect(() => {
    if (!contactId) return;
    let active = true;
    setLoading(true);
    momentService.getMomentsForContact(contactId)
      .then((data) => { if (active) setMoments(data); })
      .catch((err) => { if (active) setLoadError(err.message || 'Could not load moments.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [contactId]);

  // Only Moments that still include this person (editing may remove them).
  const shown = moments
    .filter((m) => (m.people || []).some((p) => p.id === contactId))
    .sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at));

  const defaultPeople = [{ id: contactId, full_name: lead.full_name, company: lead.company_name }];

  if (!contactId) {
    return (
      <div className="space-y-6">
        <p className="text-sm text-slate-500">
          This person has no contact record yet, so Moments cannot be linked. Their earlier memories are shown below.
        </p>
        <LegacyMemories lead={lead} user={user} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <MomentModals editor={editor} moments={moments} defaultPeople={defaultPeople} />

      <div className="flex justify-between items-center gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Moments</h3>
          <p className="text-xs text-slate-500 mt-1">Your shared history with {lead.full_name}. Private to you.</p>
        </div>
        <Button size="sm" onClick={() => setFormState({ moment: null })}>
          <SafeIcon icon={FiIcons.FiPlus} className="mr-1.5" /> Add Moment
        </Button>
      </div>

      {success && <Alert variant="success" message={success} />}
      {warning && <Alert variant="error" title="Saved with a problem" message={warning} />}
      {loadError && <Alert variant="error" title="Could not load moments" message={loadError} />}

      {!loading && shown.length > 0 && (
        <ConnectionInsights contactId={contactId} name={lead.full_name} moments={shown} onOpenMoment={setViewing} />
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Spinner size="sm" /></div>
      ) : shown.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 border border-slate-200 border-dashed rounded-xl">
          <SafeIcon icon={FiIcons.FiFeather} className="text-3xl text-slate-400 mx-auto mb-3" />
          <h4 className="text-sm font-bold text-slate-900 mb-1">No moments yet</h4>
          <p className="text-xs text-slate-500 mb-4 max-w-xs mx-auto">
            Add a Moment with {lead.full_name} to start your relationship history.
          </p>
          <Button variant="secondary" size="sm" onClick={() => setFormState({ moment: null })}>Add your first moment</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {shown.map((m) => (
            <div key={m.id} className="bg-white rounded-2xl border border-slate-200/60 p-4 shadow-sm space-y-3">
              <button type="button" onClick={() => setViewing(m)} className="block w-full text-left">
                <p className="text-xs font-medium text-slate-400">
                  {formatMomentDate(m.occurred_at)} · {formatMomentTime(m.occurred_at)}
                </p>
                {m.title && <h4 className="mt-1 text-base font-bold text-slate-900 break-words">{m.title}</h4>}
                {m.body && <p className="mt-1 text-sm text-slate-600 line-clamp-3 whitespace-pre-wrap break-words">{m.body}</p>}
                <OriginBadge moment={m} className="mt-2" />
              </button>
              <AttachmentGallery attachments={m.attachments || []} />
              {m.people?.length > 1 && (
                <p className="text-xs font-semibold text-slate-500 break-words">
                  With {m.people.map((p) => p.full_name || 'Contact').join(', ')}
                </p>
              )}
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => setFormState({ moment: m })}>Edit</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <LegacyMemories lead={lead} user={user} />
    </div>
  );
};